
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Settings, Filter, Edit, Eye, AlertTriangle, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from '@/hooks/use-mobile';

// Types
export interface Policy {
  id: number;
  name: string;
  description: string;
  status: string;
  category: string;
  impact: string;
  lastUpdated: string;
}

interface GovernancePoliciesProps {
  policies: Policy[];
}

export const getStatusBadge = (status: string) => {
  switch(status) {
    case 'Active':
      return <Badge className="bg-green-500 hover:bg-green-600">Active</Badge>;
    case 'Inactive':
      return <Badge variant="outline">Inactive</Badge>;
    case 'Draft':
      return <Badge variant="secondary">Draft</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const GovernancePolicies: React.FC<GovernancePoliciesProps> = ({ policies }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const handleCreatePolicy = () => {
    navigate('/policy-editor');
  };
  
  const handleEditPolicy = (policyId: number) => {
    navigate(`/policy-editor?id=${policyId}`);
  };

  return (
    <>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative w-full md:w-64">
            <Input 
              placeholder="Search policies..."
              className="pl-8 w-full"
            />
            <Filter className="h-4 w-4 absolute left-2.5 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          </div>
          <div className="flex gap-3">
            <Select defaultValue="all">
              <SelectTrigger className="w-full md:w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
              </SelectContent>
            </Select>
            <Select defaultValue="all-categories">
              <SelectTrigger className="w-full md:w-36">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-categories">All Categories</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="lifecycle">Lifecycle</SelectItem>
                <SelectItem value="governance">Governance</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={handleCreatePolicy} className="mt-3 md:mt-0 w-full md:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Create Policy
        </Button>
      </div>
      
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[250px]">Policy Name</TableHead>
                <TableHead className="min-w-[120px]">Category</TableHead>
                <TableHead className="min-w-[100px]">Impact</TableHead>
                <TableHead className="min-w-[100px]">Status</TableHead>
                <TableHead className="min-w-[120px]">Last Updated</TableHead>
                <TableHead className="text-right min-w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies.map((policy) => (
                <TableRow key={policy.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{policy.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{policy.description}</p>
                    </div>
                  </TableCell>
                  <TableCell>{policy.category}</TableCell>
                  <TableCell>{policy.impact}</TableCell>
                  <TableCell>{getStatusBadge(policy.status)}</TableCell>
                  <TableCell>{policy.lastUpdated}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Settings className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditPolicy(policy.id)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Flow
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {policy.status === 'Active' ? (
                            <DropdownMenuItem>
                              <AlertTriangle className="h-4 w-4 mr-2" />
                              Disable
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Activate
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </>
  );
};

export default GovernancePolicies;
